import React, { Fragment } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { UI_TEXT_NO, UI_TEXT_YES } from '../../constants/uiText'
import DesktopIndexPagination from '../DesktopIndexPagination'
import { RowActionsTrigger } from './RowActionsMenu'

/**
 * Estado vacío reutilizable de la tabla.
 * Recibe `title` y `description` como texto o JSX, e `icon` como JSX opcional.
 * También puede usarse directamente fuera de DataTable.
 */
export function DataTableEmptyState({ title, description, icon }) {
  return (
    <Box className="data-table-empty-state">
      <Box className="data-table-empty-state__content">
        {icon ? <Box className="data-table-empty-state__icon">{icon}</Box> : null}
        <Typography className="data-table-empty-state__title">{title}</Typography>
        <Typography className="data-table-empty-state__description">{description}</Typography>
      </Box>
    </Box>
  )
}

/**
 * Tabla de escritorio MUI con carga, estado vacío, acciones y filas expandidas.
 * La pantalla controla las consultas, el ordenamiento, la expansión y la paginación.
 *
 * - `columns`: `{ key, label }`, con `render(row, rowIndex)` y `headerRender(column)`
 *   opcionales. Sin render, muestra `row[key]`, booleanos como Sí/No y vacíos como «—».
 * - `rows`: datos; `rowKey`: campo único (por defecto `id`) o función `(row, index) => key`.
 * - Columnas: `align`/`headerAlign`, `width`, `className`/`headerClassName`,
 *   `cellSx`/`headerSx`, `description` (title) y `ariaSort` (sólo accesibilidad).
 *   `truncate` acorta el contenido; `getTitle(row, index)` personaliza su tooltip.
 * - Carga: `renderSkeletonRows()` > `loadingContent` > tres filas de skeleton.
 *   Vacío: `emptyContent` > `emptyState: { title, description, icon }`.
 *   `keepHeaderWhenEmpty` conserva el encabezado sin filas.
 * - Filas: `onRowClick(row, index)` y `getRowClassName(row, index)`.
 *   `actionsEnabled` agrega acciones mediante `renderRowActions(row, index)` o
 *   un botón que llama a `onOpenRowActions(event, row, index)`.
 * - Expansión: `isRowExpanded(row, index)` y `renderExpandedRow(row, index)`;
 *   `expandedRowColumnOffset` y `expandedRowClassName` ajustan el detalle.
 * - `pagination`: `{ total_count, per_page, total_pages, current_page }`.
 *   Aparece con filas, sin carga y desde `paginationVisibilityThreshold` (10).
 *   No recorta `rows`; notifica con `onPageChange(event, page)` y
 *   `onRowsPerPageChange(event, size)`.
 * - `compact` permite partir encabezados y ajusta el ancho; `variableRowHeight`
 *   activa altura automática. `minWidth` acepta píxeles o CSS.
 * Estilos base: src/css/components/DataTable.scss, cargados desde styles.scss.
 */
export default function DataTable({
  ariaLabel = 'Tabla',
  columns = [],
  rows = [],
  rowKey = 'id',
  loading = false,
  renderSkeletonRows = null,
  emptyState = null,
  emptyContent = null,
  loadingContent = null,
  keepHeaderWhenEmpty = false,
  headerBackgroundColor = '#EAF1F8',
  headerColor = '#27588D',
  minWidth,
  isRowExpanded,
  renderExpandedRow,
  expandedRowColumnOffset = 0,
  expandedRowClassName = '',
  caption,
  onRowClick,
  getRowClassName,
  pagination = null,
  onPageChange,
  onRowsPerPageChange,
  paginationAriaLabel = 'Paginacion',
  paginationSx,
  actionsEnabled = false,
  onOpenRowActions,
  renderRowActions,
  actionColumnLabel = 'ACCION',
  actionColumnWidth = 72,
  variableRowHeight = false,
  compact = false,
  paginationVisibilityThreshold = 10
}) {
  const hasRows = rows.length > 0
  const showTableStructure = loading || hasRows || keepHeaderWhenEmpty
  const colSpan = columns.length + (actionsEnabled ? 1 : 0)
  const expandedColumnOffset = Math.min(
    Math.max(0, expandedRowColumnOffset),
    Math.max(0, colSpan - 1)
  )
  const emptyStateNode = emptyContent ?? (
    <DataTableEmptyState
      title={emptyState?.title}
      description={emptyState?.description}
      icon={emptyState?.icon}
    />
  )
  const paginationTotalCount = Number(pagination?.total_count ?? rows.length) || 0
  const shouldShowPagination =
    !loading && hasRows && pagination && paginationTotalCount >= paginationVisibilityThreshold
  const tableClassName = [
    'data-table',
    compact && 'data-table--compact',
    variableRowHeight && 'data-table--variable-rows',
    expandedColumnOffset > 0 && 'data-table--expanded-offset'
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <Paper elevation={0} className="data-table-card">
      {showTableStructure ? (
        <TableContainer className="data-table-container">
          <Table
            aria-label={ariaLabel}
            aria-busy={loading}
            className={tableClassName}
            style={{
              '--data-table-min-width':
                typeof minWidth === 'number'
                  ? `${minWidth}px`
                  : (minWidth ?? (compact ? '100%' : '1180px')),
              '--data-table-header-background': headerBackgroundColor,
              '--data-table-header-color': headerColor,
              '--data-table-action-width':
                typeof actionColumnWidth === 'number' ? `${actionColumnWidth}px` : actionColumnWidth
            }}
          >
            {caption ? <caption>{caption}</caption> : null}
            <TableHead className="data-table-header">
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    align={column.headerAlign || column.align || 'left'}
                    aria-sort={column.ariaSort}
                    className={column.headerClassName}
                    title={column.description}
                    sx={{
                      width: column.width,
                      textAlign: column.headerAlign || column.align || 'left',
                      ...column.headerSx
                    }}
                  >
                    {column.headerRender ? (
                      column.headerRender(column)
                    ) : (
                      <span className="th-header-text">{column.label}</span>
                    )}
                  </TableCell>
                ))}
                {actionsEnabled ? (
                  <TableCell align="center" className="data-table-actions-cell">
                    <Box component="span" className="data-table-actions-content">
                      <span className="th-header-text">{actionColumnLabel}</span>
                    </Box>
                  </TableCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                renderSkeletonRows ? (
                  renderSkeletonRows()
                ) : loadingContent ? (
                  <TableRow>
                    <TableCell colSpan={colSpan} align="center">
                      {loadingContent}
                    </TableCell>
                  </TableRow>
                ) : (
                  Array.from({ length: 3 }, (_, index) => (
                    <TableRow key={`skeleton-${index}`} aria-hidden="true">
                      {Array.from({ length: colSpan }, (_, columnIndex) => (
                        <TableCell key={columnIndex}>
                          <Skeleton animation="wave" height={20} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )
              ) : null}
              {!loading && !hasRows ? (
                <TableRow className="data-table-empty-row">
                  <TableCell colSpan={colSpan}>{emptyStateNode}</TableCell>
                </TableRow>
              ) : null}
              {!loading &&
                rows.map((row, rowIndex) => {
                  const resolvedRowKey =
                    typeof rowKey === 'function' ? rowKey(row, rowIndex) : row?.[rowKey]

                  return (
                    <Fragment key={resolvedRowKey}>
                      <TableRow
                        className={
                          [
                            getRowClassName?.(row, rowIndex),
                            onRowClick && 'data-table-clickable-row'
                          ]
                            .filter(Boolean)
                            .join(' ') || undefined
                        }
                        onClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
                      >
                        {columns.map((column) => {
                          const rawValue = row?.[column.key]
                          const content = column.render
                            ? column.render(row, rowIndex)
                            : typeof rawValue === 'boolean'
                              ? rawValue
                                ? UI_TEXT_YES
                                : UI_TEXT_NO
                              : rawValue || rawValue === 0
                                ? rawValue
                                : '—'
                          const title = column.getTitle
                            ? column.getTitle(row, rowIndex)
                            : typeof content === 'string'
                              ? content
                              : undefined

                          return (
                            <TableCell
                              key={column.key}
                              className={
                                [column.className, column.truncate && 'data-table-truncated-cell']
                                  .filter(Boolean)
                                  .join(' ') || undefined
                              }
                              align={column.align || 'left'}
                              sx={column.cellSx}
                            >
                              {column.truncate && title ? (
                                <Tooltip title={title}>
                                  <Box component="span" className="data-table-tooltip-trigger">
                                    {content}
                                  </Box>
                                </Tooltip>
                              ) : (
                                content
                              )}
                            </TableCell>
                          )
                        })}

                        {actionsEnabled ? (
                          <TableCell align="center" className="data-table-actions-cell">
                            <Box component="span" className="data-table-actions-content">
                              {renderRowActions ? (
                                renderRowActions(row, rowIndex)
                              ) : (
                                <Tooltip title="Acciones">
                                  <span>
                                    <RowActionsTrigger
                                      label={`Acciones de ${row?.name || 'registro'}`}
                                      onClick={(event) => {
                                        event.stopPropagation()
                                        onOpenRowActions?.(event, row, rowIndex)
                                      }}
                                    />
                                  </span>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        ) : null}
                      </TableRow>
                      {renderExpandedRow && isRowExpanded?.(row, rowIndex) ? (
                        <TableRow
                          className={`data-table-expanded-row ${expandedRowClassName}`.trim()}
                        >
                          {expandedColumnOffset > 0 ? (
                            <TableCell
                              colSpan={expandedColumnOffset}
                              className="data-table-expanded-spacer"
                              aria-hidden="true"
                            />
                          ) : null}
                          <TableCell colSpan={colSpan - expandedColumnOffset}>
                            {renderExpandedRow(row, rowIndex)}
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </Fragment>
                  )
                })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        emptyStateNode
      )}

      {shouldShowPagination ? (
        <DesktopIndexPagination
          ariaLabel={paginationAriaLabel}
          pagination={pagination}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          sx={paginationSx}
        />
      ) : null}
    </Paper>
  )
}
